import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, ArrowRight, Loader2, ChefHat, ShoppingCart, Star, Quote, TrendingDown, Zap, Clock, Sun, Moon } from 'lucide-react';
import { useTheme } from '@/hooks/useTheme';
import { useAppStore } from '@/hooks/useAppStore';
import { ROUTE_PATHS } from '@/lib/index';
import { supabase } from '@/lib/supabase';
import UserMenu from '@/components/UserMenu';
import { useAuth } from '@/hooks/useAuth';
import FlydarSplash, { RadarStore } from '@/components/FlydarSplash';

const EDGE_FN = 'https://pgjncthsfxsylmvkgibx.supabase.co/functions/v1/meal-plan';
const ANON = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InBnam5jdGhzZnhzeWxtdmtnaWJ4Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzcxMDEwMTEsImV4cCI6MjA5MjY3NzAxMX0.PDtIhTOuZNnwOqNkdOD8eTC6SCXdlPT5H7vrcU30UXk';

const EXAMPLE_CODES = ['S7K 2M1', 'M5V 3A8', 'T2P 2Y5', 'V6B 1A1', 'K1P 5G3'];
const STORE_LOGOS = [
  // Empire Company
  { name: 'Sobeys',                    color: '#469E41' },
  { name: 'Safeway',                   color: '#E41720' },
  { name: 'FreshCo',                   color: '#BDD01A' },
  { name: 'IGA',                       color: '#F30017' },
  { name: 'Thrifty Foods',             color: '#005E44' },
  // Loblaw Companies
  { name: 'No Frills',                 color: '#FADC00' },
  { name: 'Real Canadian Superstore',  color: '#003DA5' },
  { name: 'Loblaws',                   color: '#000000' },
  { name: 'Zehrs',                     color: '#DA291C' },
  { name: 'T&T Supermarket',           color: '#00843D' },
  { name: 'Atlantic Superstore',       color: '#DA291C' },
  { name: 'Maxi',                      color: '#0076A8' },
  // Metro Inc.
  { name: 'Metro',                     color: '#D81E05' },
  { name: 'Food Basics',               color: '#FFB500' },
  // Independents
  { name: 'Walmart',                   color: '#0071CE' },
  { name: 'Giant Tiger',               color: '#C8102E' },
  { name: 'Save-On-Foods',             color: '#00551B' },
  { name: 'Co-op',                     color: '#CC0000' },
];
const SAMPLE_MEALS = [
  { name: 'Honey Garlic Butter Salmon', time: '25 min', cost: '$4.20/srv', cat: 'Chef', emoji: '🐟', deal: 'Salmon $7.99/lb — on sale' },
  { name: 'Cheeseburger Pasta Skillet', time: '25 min', cost: '$2.60/srv', cat: 'Basics', emoji: '🍝', deal: 'Ground beef $5.49/lb — on sale' },
  { name: 'Frozen Fish & Oven Fries', time: '30 min', cost: '$2.48/srv', cat: 'Lazy Night', emoji: '🐠', deal: 'Breaded fish $5.98 — on sale' },
];
const FALLBACK_REVIEWS = [
  { id: 'f1', user_name: 'Sarah M.', meal_name: 'Honey Garlic Chicken Thighs', rating: 5, comment: 'Saved $28 this week! The recipes are actually delicious and so easy to follow.', store_name: 'Sobeys' },
  { id: 'f2', user_name: 'James T.', meal_name: 'Sheet Pan Salmon', rating: 5, comment: 'Never thought I could eat this well on a budget. Restaurant quality from flyer deals.', store_name: 'No Frills' },
  { id: 'f3', user_name: 'Priya K.', meal_name: 'Beef Tacos al Carbon', rating: 5, comment: 'My whole family loves flyer night now. The kids pick the meals and we save money!', store_name: 'Food Basics' },
  { id: 'f4', user_name: 'Mike D.', meal_name: 'Garlic Butter Pork Tenderloin', rating: 5, comment: 'Better than HelloFresh and I\'m using actual deals from my local store.', store_name: 'Metro' },
  { id: 'f5', user_name: 'Amanda L.', meal_name: 'One-Pan Lemon Chicken', rating: 5, comment: 'Grocery bill went from $200 to under $120/week. This app pays for itself instantly.', store_name: 'Loblaws' },
];

interface Review { id: string; user_name: string | null; meal_name: string; rating: number; comment: string | null; store_name?: string | null; }

function ReviewsCarousel() {
  const [reviews, setReviews] = useState<Review[]>(FALLBACK_REVIEWS);
  const [idx, setIdx] = useState(0);
  const [dir, setDir] = useState(1);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    supabase.from('dish_reviews').select('id,user_name,meal_name,rating,comment,store_name')
      .eq('is_hidden', false).gte('rating', 4).order('created_at', { ascending: false }).limit(15)
      .then(({ data }) => { if (data && data.length >= 2) setReviews(data as Review[]); });
  }, []);

  const advance = useCallback((d: number) => {
    setDir(d); setIdx(i => (i + d + reviews.length) % reviews.length);
  }, [reviews.length]);

  useEffect(() => {
    timer.current = setTimeout(() => advance(1), 4500);
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [idx, advance]);

  const r = reviews[idx];
  return (
    <div>
      <div className="overflow-hidden" style={{ minHeight: 180 }}>
        <AnimatePresence mode="wait" custom={dir}>
          <motion.div key={r.id} custom={dir}
            variants={{ enter: (d: number) => ({ opacity: 0, x: d > 0 ? 48 : -48 }), center: { opacity: 1, x: 0 }, exit: (d: number) => ({ opacity: 0, x: d > 0 ? -48 : 48 }) }}
            initial="enter" animate="center" exit="exit" transition={{ duration: 0.32, ease: 'easeInOut' }}>
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
              <Quote className="w-6 h-6 text-primary/40 mb-3" />
              <p className="text-white/85 text-sm sm:text-base leading-relaxed font-medium mb-5">"{r.comment || 'Great experience!'}"</p>
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-full bg-primary/20 border border-primary/30 flex items-center justify-center text-primary font-black text-xs">
                    {(r.user_name || 'A').slice(0, 2).toUpperCase()}
                  </div>
                  <div>
                    <p className="text-white font-bold text-xs">{r.user_name || 'Anonymous'}</p>
                    <p className="text-white/40 text-[11px]">{r.store_name || 'FlyersFeast'} · {r.meal_name}</p>
                  </div>
                </div>
                <div className="flex gap-0.5">{[1,2,3,4,5].map(n => <Star key={n} className={`w-3.5 h-3.5 ${n <= r.rating ? 'fill-amber-400 text-amber-400' : 'text-white/20'}`} />)}</div>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
      <div className="flex items-center justify-center gap-2 mt-4">
        <button onClick={() => advance(-1)} className="w-7 h-7 rounded-full bg-white/8 hover:bg-white/15 flex items-center justify-center text-white/60 hover:text-white text-lg">‹</button>
        <div className="flex gap-1.5">{reviews.map((_, i) => <button key={i} onClick={() => { setDir(i > idx ? 1 : -1); setIdx(i); }} className={`rounded-full transition-all ${i === idx ? 'bg-primary w-4 h-2' : 'bg-white/20 w-2 h-2'}`} />)}</div>
        <button onClick={() => advance(1)} className="w-7 h-7 rounded-full bg-white/8 hover:bg-white/15 flex items-center justify-center text-white/60 hover:text-white text-lg">›</button>
      </div>
    </div>
  );
}

export default function HomePage() {
  const [postal, setPostal] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [focused, setFocused] = useState(false);
  // Flydar radar state
  const [radarStores, setRadarStores] = useState<RadarStore[]>([]);
  const [showRadar, setShowRadar] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const { setPostalCode, setStores, setStep, selectStore } = useAppStore();
  const { showAuthModal: _showAuthModal, setShowAuthModal } = useAuth();
  const navigate = useNavigate();
  const { dark, toggle } = useTheme();

  const fmt = (v: string) => {
    const c = v.toUpperCase().replace(/[^A-Z0-9]/g, '');
    return c.length > 3 ? c.slice(0, 3) + ' ' + c.slice(3, 6) : c;
  };

  const handleSubmit = async (e: React.FormEvent | null, overridePostal?: string) => {
    e?.preventDefault();
    const p = (overridePostal ?? postal).trim();
    if (!p || p.replace(/\s/g,'').length < 6) { setErr('Enter a valid Canadian postal code (e.g. S7K 2M1)'); return; }
    setLoading(true); setErr('');
    try {
      setPostalCode(p);
      const res = await fetch(EDGE_FN, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${ANON}` },
        body: JSON.stringify({ action: 'stores', postalCode: p }),
      });
      const data = await res.json();
      const stores = data?.stores ?? data?._oma_data?.stores ?? [];
      if (!stores.length) throw new Error('No stores found — try a different postal code');
      setStores(stores);
      setStep('stores');
      // Show the FLYDAR radar with real store data and full store objects
      setRadarStores(stores.map((s: { name: string; dealCount?: number; color?: string; emoji?: string }) => ({
        name: s.name, dealCount: s.dealCount ?? 0, color: s.color, emoji: s.emoji,
      })));
      setShowRadar(true);
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : 'Something went wrong. Please try again.');
      setLoading(false);
    }
    // Note: setLoading(false) intentionally not called on success — radar takes over
  };

  // Scrolling store ticker
  const doubled = [...STORE_LOGOS, ...STORE_LOGOS];

  return (
    <>
    {/* FLYDAR radar — blip tap → direct to store flyer, onDone → Compare Stores */}
    <AnimatePresence>
      {showRadar && (
        <FlydarSplash
          stores={radarStores}
          postalCode={postal}
          onDone={() => { setShowRadar(false); navigate(ROUTE_PATHS.STORES); }}
          onSelectStore={(store) => {
            // Find full store obj from appStore and select it, then go straight to scanning
            const fullStore = { name: store.name, dealCount: store.dealCount, color: store.color, emoji: store.emoji };
            selectStore(fullStore);
            setShowRadar(false);
            navigate(`${ROUTE_PATHS.LOADING}?postal=${encodeURIComponent(postal)}&merchant=${encodeURIComponent(store.name)}`);
          }}
        />
      )}
    </AnimatePresence>

    <div className="min-h-screen bg-background">

      {/* ── HERO ── */}
      <div className="relative overflow-hidden min-h-[100dvh] flex flex-col" style={{ background: '#111' }}>

        {/* Food background photo */}
        <div className="absolute inset-0"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1690983322857-0811d47fedfc?w=1400&q=80')`,
            backgroundSize: 'cover', backgroundPosition: 'center right',
            filter: 'saturate(0.7) brightness(0.75)',
          }} />
        {/* Gradient overlay — dark left, fades right */}
        <div className="absolute inset-0"
          style={{ background: 'linear-gradient(105deg, rgba(17,17,17,1.0) 0%, rgba(17,17,17,0.88) 40%, rgba(17,17,17,0.45) 65%, rgba(17,17,17,0.10) 100%)' }} />

        {/* Nav */}
        <header className="relative z-10 flex items-center justify-between px-5 pt-5 pb-2 w-full"
          style={{ background: 'rgba(17,17,17,0.6)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="flex items-center gap-1.5 select-none">
            <span className="text-sm font-black tracking-tight text-white uppercase">FLYERS FEAST</span>
            <span
              className="text-xs font-black tracking-widest uppercase"
              style={{ color: '#4ade80', textShadow: '0 0 10px rgba(74,222,128,0.75), 0 0 22px rgba(74,222,128,0.4)' }}
            >BETA</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={toggle}
              className="w-8 h-8 rounded-lg bg-white/8 hover:bg-white/15 flex items-center justify-center transition-colors"
              title={dark ? 'Switch to light mode' : 'Switch to dark mode'}>
              {dark ? <Sun className="w-4 h-4 text-amber-400" /> : <Moon className="w-4 h-4 text-white/60" />}
            </button>
            <UserMenu />
          </div>
        </header>

        {/* Hero content — LEFT aligned */}
        <div className="relative z-10 flex-1 flex flex-col justify-center px-6 sm:px-10 pt-10 pb-8 max-w-2xl">

          {/* Pill badge */}
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="inline-flex items-center gap-2 self-start bg-primary/15 border border-primary/25 text-primary text-[11px] font-bold px-3.5 py-1.5 rounded-full mb-6 uppercase tracking-widest">
            <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
            AI-Powered · Real Flyer Deals
          </motion.div>

          {/* Headline */}
          <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.18 }}
            className="text-5xl sm:text-6xl font-black leading-[1.02] tracking-tighter uppercase mb-6">
            <span className="text-white block">Your store's deals.</span>
            <span className="text-primary block">We imagine the meals.</span>
          </motion.h1>

          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.28 }}
            className="text-white/55 text-base leading-relaxed max-w-md mb-8">
            Enter your postal code. We scan this week's real grocery flyers and AI builds
            complete recipes around what's on sale — with prices and a ready-to-shop list.
          </motion.p>

          {/* Postal input */}
          <motion.form initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}
            onSubmit={e => handleSubmit(e)} className="w-full max-w-sm">
            <div className={`flex items-center bg-white/8 border rounded-2xl overflow-hidden transition-all backdrop-blur-sm ${focused ? 'border-primary/60 shadow-[0_0_0_3px_rgba(34,197,94,0.15)]' : 'border-white/12'}`}>
              <MapPin className={`w-4 h-4 shrink-0 ml-4 transition-colors ${focused ? 'text-primary' : 'text-white/30'}`} />
              <input
                ref={inputRef}
                value={postal}
                onChange={e => { setPostal(fmt(e.target.value)); setErr(''); }}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                placeholder="S7K 2M1"
                maxLength={7}
                autoComplete="postal-code"
                className="flex-1 bg-transparent text-white placeholder-white/25 text-base font-mono font-bold tracking-widest focus:outline-none min-w-0 px-3 py-4"
              />
              <button type="submit" disabled={loading}
                className="flex items-center gap-1.5 bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-bold px-5 py-4 transition-all disabled:opacity-60 active:scale-95 shrink-0">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><span>Find Deals</span><ArrowRight className="w-4 h-4" /></>}
              </button>
            </div>
            {err && <p className="text-red-400 text-xs mt-2 ml-1">{err}</p>}
            <div className="flex items-center gap-2 mt-3 flex-wrap">
              <span className="text-white/25 text-xs">Try:</span>
              {EXAMPLE_CODES.slice(0, 4).map(c => (
                <button key={c} type="button" onClick={() => { setPostal(c); handleSubmit(null, c); }}
                  className="text-white/40 hover:text-primary text-xs font-mono transition-colors">{c}</button>
              ))}
            </div>
          </motion.form>

          {/* Stats */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
            className="flex items-center gap-8 mt-10 pt-8 border-t border-white/10">
            {[{ val: '16+', label: 'Canadian chains' }, { val: '~$40', label: 'Avg. weekly saving' }, { val: '7', label: 'Meals per plan' }].map(s => (
              <div key={s.label}>
                <p className="text-white text-2xl font-black">{s.val}</p>
                <p className="text-white/35 text-[11px] font-medium mt-0.5">{s.label}</p>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Store ticker */}
        <div className="relative z-10 overflow-hidden py-3.5 border-t border-primary/10" style={{ background: 'rgba(13,31,18,0.85)' }}>
          <motion.div className="flex gap-5 whitespace-nowrap"
            animate={{ x: ['0%', '-50%'] }} transition={{ duration: 30, repeat: Infinity, ease: 'linear' }}>
            {doubled.map((s, i) => (
              <div key={i} className="inline-flex items-center gap-2 bg-white/5 border border-white/8 rounded-xl px-3.5 py-2 shrink-0">
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: s.color }} />
                <span className="text-white/50 text-xs font-semibold">{s.name}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* ── How it works ── */}
      <section className="py-16 px-5 bg-muted/30 border-b border-border/50">
        <div className="max-w-2xl mx-auto">
          <p className="text-xs font-bold text-primary uppercase tracking-widest text-center mb-2">How it works</p>
          <h2 className="text-2xl font-black text-foreground text-center mb-10">From flyer to table in 3 steps</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            {[
              { step: '01', icon: MapPin, title: 'Enter your postal code', desc: 'We find every grocery store near you and pull this week\'s live flyer deals.' },
              { step: '02', icon: Zap, title: 'AI builds your plan', desc: 'Chef Blaze creates 7 real dinners — Chef, Basics, and Lazy Night — built around what\'s on sale.' },
              { step: '03', icon: ShoppingCart, title: 'Cook & save', desc: 'Get step-by-step recipes, a categorised grocery list, nutrition info, and exact cost per serving.' },
            ].map(({ step, icon: Icon, title, desc }) => (
              <div key={step} className="bg-card border border-border rounded-2xl p-5">
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-[11px] font-black text-primary/50 font-mono">{step}</span>
                  <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Icon className="w-4 h-4 text-primary" />
                  </div>
                </div>
                <p className="font-bold text-foreground text-sm mb-1">{title}</p>
                <p className="text-muted-foreground text-xs leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Sample meal cards ── */}
      <section className="py-16 px-5">
        <div className="max-w-2xl mx-auto">
          <p className="text-xs font-bold text-primary uppercase tracking-widest text-center mb-2">This week's plan</p>
          <h2 className="text-2xl font-black text-foreground text-center mb-8">3 categories, every week</h2>
          <div className="space-y-3">
            {SAMPLE_MEALS.map((m, i) => {
              const catColor = i === 0 ? 'bg-purple-500/10 text-purple-400 border-purple-500/20' : i === 1 ? 'bg-blue-500/10 text-blue-400 border-blue-500/20' : 'bg-amber-500/10 text-amber-400 border-amber-500/20';
              return (
                <motion.div key={m.name} initial={{ opacity: 0, x: -16 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                  className="bg-card border border-border rounded-2xl p-4 flex items-center gap-3.5">
                  <div className="w-11 h-11 rounded-xl bg-muted flex items-center justify-center text-2xl shrink-0">{m.emoji}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                      <span className="font-bold text-foreground text-sm truncate">{m.name}</span>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${catColor} shrink-0`}>{m.cat}</span>
                    </div>
                    <div className="flex items-center gap-3 text-muted-foreground text-[11px]">
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{m.time}</span>
                      <span className="flex items-center gap-1"><TrendingDown className="w-3 h-3 text-green-500" /><span className="text-green-600 font-semibold">{m.cost}</span></span>
                    </div>
                    <p className="text-[11px] text-primary mt-0.5">🏷️ {m.deal}</p>
                  </div>
                  <ChefHat className="w-4 h-4 text-muted-foreground/40 shrink-0" />
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Reviews ── */}
      <section className="py-16 px-5 bg-muted/30 border-y border-border/50">
        <div className="max-w-lg mx-auto">
          <p className="text-xs font-bold text-primary uppercase tracking-widest text-center mb-2">Real families</p>
          <h2 className="text-2xl font-black text-foreground text-center mb-8">What Canadians are saying</h2>
          <ReviewsCarousel />
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section className="py-16 px-5 text-center border-b border-border/50">
        <div className="max-w-sm mx-auto">
          <div className="flex items-center justify-center gap-1.5 mb-4 select-none">
            <span className="text-xl font-black tracking-tight text-foreground uppercase">FLYERS FEAST</span>
            <span
              className="text-sm font-black tracking-widest uppercase"
              style={{ color: '#4ade80', textShadow: '0 0 10px rgba(74,222,128,0.75), 0 0 22px rgba(74,222,128,0.4)' }}
            >BETA</span>
          </div>
          <h2 className="text-2xl font-black text-foreground mb-2">Ready to start saving?</h2>
          <p className="text-muted-foreground text-sm mb-6">Enter your postal code and get this week's meal plan in seconds.</p>
          <button onClick={() => { window.scrollTo({ top: 0, behavior: 'smooth' }); setTimeout(() => inputRef.current?.focus(), 600); }}
            className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-primary-foreground font-bold px-6 py-3.5 rounded-2xl transition-all active:scale-95">
            <MapPin className="w-4 h-4" /> Get My Meal Plan
          </button>
          <p className="text-muted-foreground/50 text-[11px] mt-4">Free · No signup required · Updates weekly</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-5 bg-muted/20 border-t border-border/30 text-center">
        <div className="flex items-center justify-center gap-1.5 mb-2 select-none">
          <span className="font-black text-foreground text-sm uppercase tracking-tight">FLYERS FEAST</span>
          <span
            className="text-xs font-black tracking-widest uppercase"
            style={{ color: '#4ade80', textShadow: '0 0 8px rgba(74,222,128,0.7), 0 0 18px rgba(74,222,128,0.35)' }}
          >BETA</span>
        </div>
        <p className="text-muted-foreground/50 text-xs">Your store's deals, we imagine the meals · Saskatchewan, Canada</p>
        <div className="flex items-center justify-center gap-4 mt-3">
          {['Deals', 'My Pantry', 'Reviews'].map(link => (
            <button key={link} onClick={() => { if (link === 'Reviews') navigate(ROUTE_PATHS.REVIEWS); else if (link === 'Deals') navigate(ROUTE_PATHS.FLYER); else navigate(ROUTE_PATHS.MY_PANTRY); }}
              className="text-muted-foreground hover:text-foreground text-xs transition-colors">{link}</button>
          ))}
          <button onClick={() => setShowAuthModal(true)} className="text-muted-foreground hover:text-foreground text-xs transition-colors">Sign In</button>
        </div>
      </footer>
    </div>
    </>
  );
}
